import React, { forwardRef, useMemo } from 'react';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import { KingBed, SingleBed, MeetingRoom, CleaningServices, NoMeetingRoom, Star } from '@mui/icons-material';
import { LEFT_COL_WIDTH } from '../constants/layout';
import { getRoomStatus } from '../utils/roomStatus';

// Icono por tipo de habitación
const getRoomIcon = (type) => {
  const t = type?.toLowerCase() || '';
  if (t.includes('king') || t.includes('suite')) return <KingBed fontSize="small" />;
  if (t.includes('single')) return <SingleBed fontSize="small" />;
  return <MeetingRoom fontSize="small" />;
};

const RoomsSidebar = forwardRef(function RoomsSidebar(
  { rooms, reservationsByRoom, height, rowHeight, onSyncScroll, pmsColors },
  listRef
) {
  const handleScroll = ({ scrollOffset, scrollUpdateWasRequested }) => {
    if (!scrollUpdateWasRequested) onSyncScroll(scrollOffset);
  };

  // Resumen del inventario
  const summary = useMemo(() => {
    const totals = { total: rooms.length, occupied: 0, clean: 0, dirty: 0, ooo: 0 };
    rooms.forEach((room) => {
      const resv = reservationsByRoom.get(String(room.roomNo)) || [];
      const st = getRoomStatus(room, resv, undefined);
      if (st === 'occupied' || st === 'arriving') totals.occupied++;
      else if (room.status === 'out_of_order') totals.ooo++;
      else if (room.condition === 'dirty' || st === 'dirty') totals.dirty++;
      else totals.clean++;
    });
    return totals;
  }, [rooms, reservationsByRoom]);

  return (
    <Box
      sx={{
        width: LEFT_COL_WIDTH,
        borderRight: `1px solid ${pmsColors.ui.border}`,
        backgroundColor: pmsColors.ui.sidebar,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header del sidebar — se alinea con los 4 headers del timeline (month+week+day+rate) */}
      <Box
        sx={{
          height: 28 + 22 + 46 + 28, // MONTH + WEEK + DAY + RATE
          borderBottom: `1px solid ${pmsColors.ui.border}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          p: '8px 14px 10px',
          background: `linear-gradient(180deg, ${pmsColors.ui.surface}, ${pmsColors.ui.sidebar})`
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.8px',
            color: pmsColors.text.secondary,
            fontSize: '0.65rem',
            textTransform: 'uppercase'
          }}
        >
          Habitaciones
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: '1rem', color: pmsColors.text.primary }}>
          {summary.total}
          <Box component="span" sx={{ ml: 0.75, color: pmsColors.text.secondary, fontWeight: 600, fontSize: '0.72rem' }}>
            · {summary.occupied} ocupadas
          </Box>
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
          <Tooltip title="Ocupación">
            <Box
              sx={{
                fontSize: '10.5px',
                color: pmsColors.text.secondary,
                px: 0.75,
                py: 0.25,
                bgcolor: pmsColors.ui.background,
                borderRadius: 0.75,
                fontWeight: 600
              }}
            >
              <b style={{ color: pmsColors.text.primary }}>
                {summary.total > 0 ? Math.round((summary.occupied / summary.total) * 100) : 0}%
              </b>{' '}
              ocup.
            </Box>
          </Tooltip>
          <Box
            sx={{
              fontSize: '10.5px',
              color: pmsColors.text.secondary,
              px: 0.75,
              py: 0.25,
              bgcolor: pmsColors.ui.background,
              borderRadius: 0.75,
              fontWeight: 600
            }}
          >
            <b style={{ color: pmsColors.text.primary }}>{summary.clean}</b> limpias
          </Box>
          {summary.ooo > 0 && (
            <Box
              sx={{
                fontSize: '10.5px',
                color: '#991B1B',
                px: 0.75,
                py: 0.25,
                bgcolor: '#FEE2E2',
                borderRadius: 0.75,
                fontWeight: 700
              }}
            >
              <b>{summary.ooo}</b> OOO
            </Box>
          )}
        </Stack>
      </Box>

      <List
        height={height}
        itemCount={rooms.length}
        itemSize={rowHeight}
        width={LEFT_COL_WIDTH}
        overscanCount={5}
        onScroll={handleScroll}
        ref={listRef}
      >
        {({ index, style }) => {
          const room = rooms[index];
          const roomReservations = reservationsByRoom.get(String(room.roomNo)) || [];
          const status = getRoomStatus(room, roomReservations, undefined);

          // Mapeo de estado -> color (con fallback a la paleta PMS)
          const statusColor =
            room.status === 'out_of_order'
              ? pmsColors.rooms.out_of_order
              : room.status === 'maintenance'
              ? pmsColors.rooms.maintenance
              : status === 'occupied' || status === 'arriving'
              ? pmsColors.rooms.occupied
              : room.condition === 'dirty' || status === 'dirty'
              ? pmsColors.rooms.vacant_dirty
              : pmsColors.rooms.vacant_clean;

          const isDirty = room.condition === 'dirty' || status === 'dirty';
          const isOOO = room.status === 'out_of_order';
          const isVip = roomReservations.some((r) => r.vip);

          return (
            <Box
              style={style}
              sx={{
                display: 'grid',
                gridTemplateColumns: '4px 28px 1fr auto',
                alignItems: 'center',
                gap: 1.25,
                pr: 1.5,
                borderBottom: `1px solid ${pmsColors.ui.border}`,
                backgroundColor: pmsColors.ui.surface,
                transition: 'background-color 0.15s',
                '&:hover': { backgroundColor: pmsColors.ui.hover },
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Stripe de estado */}
              <Box sx={{ width: 4, height: '60%', borderRadius: '0 3px 3px 0', backgroundColor: statusColor }} />

              {/* Icono tipo de habitación */}
              <Box sx={{ color: pmsColors.text.secondary, display: 'flex', justifyContent: 'center' }}>
                {getRoomIcon(room.roomType)}
              </Box>

              {/* Nº + tipo */}
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: pmsColors.text.primary,
                      fontSize: '0.88rem',
                      lineHeight: 1.1
                    }}
                  >
                    {room.roomNo}
                  </Typography>
                  {isVip && (
                    <Tooltip title="VIP">
                      <Star sx={{ fontSize: 12, color: '#D97706' }} />
                    </Tooltip>
                  )}
                  {isDirty && (
                    <Tooltip title="Sucia">
                      <CleaningServices sx={{ fontSize: 12, color: pmsColors.rooms.vacant_dirty }} />
                    </Tooltip>
                  )}
                  {isOOO && (
                    <Tooltip title="Fuera de Servicio">
                      <NoMeetingRoom sx={{ fontSize: 12, color: pmsColors.rooms.out_of_order }} />
                    </Tooltip>
                  )}
                </Stack>
                <Typography
                  variant="caption"
                  sx={{
                    color: pmsColors.text.secondary,
                    fontSize: '0.68rem',
                    display: 'block',
                    lineHeight: 1.2,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                >
                  {room.roomType || 'Standard'}
                </Typography>
              </Box>

              {/* Contador de reservas */}
              {roomReservations.length > 0 && (
                <Tooltip title={`${roomReservations.length} reserva(s)`}>
                  <Box
                    sx={{
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      borderRadius: '9px',
                      backgroundColor: pmsColors.ui.background,
                      color: pmsColors.text.primary,
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {roomReservations.length}
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        }}
      </List>
    </Box>
  );
});

export default RoomsSidebar;
